// ==================================================
// FILE: themes/vanillaEvents.js
// ==================================================

// CONTENT LIBRARY - Pure narrative and event data for the Vanilla universe
// Generic military XCOM-style tactical operations
// The game engine draws from these pools to generate and flavorize events

/**
 * BACKDROP EVENTS
 * Universe texture and world-building - paint the picture of a military operation
 * Used by engine for atmospheric events that reinforce setting and lore
 */

export const BACKDROP_EVENTS = [
  {
    id: 'enemy_movement',
    category: 'backdrop',
    title: 'Enemy Force Repositioning',
    description: 'Intelligence reports enemy units shifting positions in your operational theater. They\'re adapting to our tactics.',
    narrative: 'The enemy learns. Every strike we make, they counter. This is war.',
    rewards: { intel: 8 },
    tags: ['enemy_presence', 'tactical'],
    storyHooks: []
  },
  {
    id: 'supply_delivery',
    category: 'backdrop',
    title: 'Supply Convoy Arrived',
    description: 'Scheduled resupply convoy delivered ammunition, equipment, and provisions to the base.',
    narrative: 'The logistics chain holds. Without supplies, we\'re just soldiers with empty magazines.',
    rewards: { supplies: 50 },
    tags: ['logistics', 'routine'],
    storyHooks: []
  },
  {
    id: 'terrain_advantage',
    category: 'backdrop',
    title: 'Favorable Terrain Identified',
    description: 'Reconnaissance teams mapped high-ground positions that provide tactical advantage in the operational area.',
    narrative: 'Knowledge of the battlefield is half the battle. The rest is execution.',
    rewards: { intel: 5 },
    tags: ['reconnaissance', 'tactical'],
    storyHooks: []
  },
  {
    id: 'allied_communication',
    category: 'backdrop',
    title: 'Allied Base Communication',
    description: 'Received secure transmission from allied command. They\'re holding their positions and sharing intelligence.',
    narrative: 'We\'re not alone in this fight. Across the theater, others stand with us.',
    rewards: { intel: 7, funds: 500 },
    tags: ['allies', 'coordination'],
    storyHooks: ['allied_network']
  },
  {
    id: 'weapon_cache',
    category: 'backdrop',
    title: 'Weapons Cache Discovered',
    description: 'Patrol discovered an abandoned enemy weapons cache. Equipment is intact and usable.',
    narrative: 'The enemy\'s loss is our gain. Every rifle we recover is one less pointed at us.',
    rewards: { supplies: 75, intel: 6 },
    tags: ['salvage', 'opportunity'],
    storyHooks: []
  },
  {
    id: 'civilian_intel',
    category: 'backdrop',
    title: 'Civilian Intelligence Report',
    description: 'Local civilians provided information on enemy patrol routes and supply lines in exchange for protection.',
    narrative: 'Hearts and minds. The local population sees us as protectors. That\'s a tactical advantage.',
    rewards: { intel: 12 },
    tags: ['civilians', 'intelligence'],
    storyHooks: ['civilian_support']
  },
  {
    id: 'weather_advantage',
    category: 'backdrop',
    title: 'Weather Window Identified',
    description: 'Meteorology reports favorable weather conditions for the next 48 hours. Perfect for operations.',
    narrative: 'Clear skies mean air support. Rain means mud and misery. We take what we can get.',
    rewards: { intel: 4 },
    tags: ['environment', 'tactical'],
    storyHooks: []
  },
  {
    id: 'propaganda_success',
    category: 'backdrop',
    title: 'Propaganda Campaign Success',
    description: 'Our information operations are working. Enemy morale is declining, and desertions are increasing.',
    narrative: 'We fight with bullets and with words. Sometimes the latter cuts deeper.',
    rewards: { intel: 10, funds: 1000 },
    tags: ['psychological', 'morale'],
    storyHooks: []
  },
  {
    id: 'equipment_upgrade',
    category: 'backdrop',
    title: 'Equipment Upgrade Package',
    description: 'Command authorized delivery of upgraded equipment package. New optics, armor plates, and tactical gear.',
    narrative: 'Better gear means better odds. Command is investing in us. Let\'s make it count.',
    rewards: { supplies: 60 },
    tags: ['equipment', 'command'],
    storyHooks: []
  },
  {
    id: 'enemy_deserter',
    category: 'backdrop',
    title: 'Enemy Deserter Interrogated',
    description: 'An enemy soldier defected to our position. Intelligence debriefing reveals valuable tactical information.',
    narrative: 'Not everyone on the other side believes in their cause. That\'s something we can exploit.',
    rewards: { intel: 15, funds: 500 },
    tags: ['intelligence', 'defection'],
    storyHooks: ['enemy_morale']
  }
];

/**
 * SCHEDULED EVENTS
 * Regular, predictable resource delivery and logistics events
 */
export const SCHEDULED_EVENTS = [
  {
    id: 'supply_drop',
    category: 'scheduled',
    schedulePattern: 'every_7_days',
    title: 'Weekly Resupply',
    description: 'Scheduled supply drop delivered via logistics convoy. Ammunition, rations, and equipment replenished.',
    narrative: 'Another week, another resupply. The chain holds, and we fight on.',
    rewards: { supplies: 50, funds: 5000 },
    tags: ['logistics', 'routine'],
    storyHooks: []
  },
  {
    id: 'intel_briefing',
    category: 'scheduled',
    schedulePattern: 'every_5_days',
    title: 'Intelligence Briefing',
    description: 'Intelligence officers provide updated threat assessment and enemy movement analysis.',
    narrative: 'Knowledge is ammunition. The more we know, the better we fight.',
    rewards: { intel: 15 },
    tags: ['intelligence', 'briefing'],
    storyHooks: []
  },
  {
    id: 'training_progress',
    category: 'scheduled',
    schedulePattern: 'every_10_days',
    title: 'Training Cycle Complete',
    description: 'Squad training exercises completed. Soldiers demonstrate improved combat effectiveness.',
    narrative: 'We drill, we train, we prepare. When the bullets fly, training becomes instinct.',
    rewards: { supplies: 25 },
    tags: ['training', 'readiness'],
    storyHooks: []
  }
];

/**
 * THREAT EVENTS
 * Escalating danger tied to threat level and enemy escalation
 */
export const THREAT_EVENTS = [
  {
    id: 'enemy_patrol',
    category: 'threat',
    threatIntensity: 'low',
    title: 'Increased Enemy Patrols',
    description: 'Reconnaissance reports heightened enemy patrol activity near base perimeter. They\'re probing our defenses.',
    narrative: 'They\'re testing us. Looking for weaknesses. We show them none.',
    threat: true,
    tags: ['enemy_presence', 'perimeter'],
    storyHooks: []
  },
  {
    id: 'enemy_offensive',
    category: 'threat',
    threatIntensity: 'medium',
    title: 'Enemy Offensive Preparation',
    description: 'Intelligence confirms enemy forces massing for coordinated assault on allied positions.',
    narrative: 'They\'re coming. Not if, but when. We prepare accordingly.',
    threat: true,
    tags: ['enemy_presence', 'escalation'],
    storyHooks: ['enemy_escalation']
  },
  {
    id: 'command_breakdown',
    category: 'threat',
    threatIntensity: 'high',
    title: 'Command Network Disruption',
    description: 'Enemy jamming operations disrupting communications network. Coordination with allied forces compromised.',
    narrative: 'Cut off. Isolated. This is when leadership matters most.',
    threat: true,
    tags: ['command', 'isolation'],
    storyHooks: ['communication_crisis']
  },
  {
    id: 'supply_interdiction',
    category: 'threat',
    threatIntensity: 'medium',
    title: 'Supply Route Compromised',
    description: 'Enemy ambushed supply convoy en route to base. Scheduled deliveries delayed or destroyed.',
    narrative: 'They hit our logistics. Smart. Now we fight hungry.',
    cost: { supplies: 30 },
    tags: ['logistics', 'enemy_tactics'],
    storyHooks: []
  },
  {
    id: 'infiltrator_warning',
    category: 'threat',
    threatIntensity: 'high',
    title: 'Potential Infiltrator',
    description: 'Security detected suspicious behavior among base personnel. Possible enemy infiltration.',
    narrative: 'Paranoia or preparation? In war, the line between them is thin.',
    threat: true,
    tags: ['internal', 'security'],
    storyHooks: ['infiltration_arc']
  },
  {
    id: 'morale_crisis',
    category: 'threat',
    threatIntensity: 'high',
    title: 'Morale Declining',
    description: 'After consecutive losses, soldiers are questioning the mission. Leadership must respond.',
    narrative: 'We\'ve taken hits. People are scared. This is when we prove what we\'re made of.',
    threat: true,
    tags: ['morale', 'leadership'],
    storyHooks: ['leadership_test']
  },
  {
    id: 'ammunition_shortage',
    category: 'threat',
    threatIntensity: 'medium',
    title: 'Ammunition Shortage',
    description: 'Extended operations depleted ammunition reserves faster than expected. Rationing required.',
    narrative: 'Every bullet counts. Make them matter.',
    cost: { supplies: 20 },
    tags: ['logistics', 'resources'],
    storyHooks: []
  }
];

/**
 * MORALE EVENTS
 * Positive moments, victories, inspirational breakthroughs
 */
export const MORALE_EVENTS = [
  {
    id: 'mission_success',
    category: 'morale',
    moraleType: 'minor_victory',
    title: 'Mission Success',
    description: 'Squad executed flawless operation. All objectives achieved, zero casualties. Morale soaring.',
    narrative: 'This is what we train for. Perfect execution. Everyone comes home.',
    bonus: true,
    rewards: { funds: 500 },
    tags: ['victory', 'success'],
    storyHooks: []
  },
  {
    id: 'hero_emerges',
    category: 'morale',
    moraleType: 'major_inspiration',
    title: 'Soldier Commendation',
    description: 'A soldier\'s extraordinary actions in combat earned formal commendation. Their bravery inspires the entire base.',
    narrative: 'Heroes are made in moments like these. One soldier\'s courage becomes everyone\'s motivation.',
    bonus: true,
    rewards: { intel: 5, funds: 1000 },
    tags: ['heroism', 'inspiration'],
    storyHooks: ['hero_soldier']
  },
  {
    id: 'tactical_innovation',
    category: 'morale',
    moraleType: 'major_victory',
    title: 'Tactical Breakthrough',
    description: 'Analysts developed new tactical doctrine that dramatically improves combat effectiveness.',
    narrative: 'Innovation under pressure. This is how we win—by adapting faster than the enemy.',
    bonus: true,
    rewards: { intel: 20, supplies: 40 },
    tags: ['innovation', 'tactics'],
    storyHooks: []
  },
  {
    id: 'allied_coordination',
    category: 'morale',
    moraleType: 'major_inspiration',
    title: 'Allied Operation Success',
    description: 'Coordinated operation with allied forces achieved decisive victory. Unity proves effective.',
    narrative: 'Together, we are stronger. This is the power of coordinated military action.',
    bonus: true,
    rewards: { intel: 10, funds: 2000 },
    tags: ['allies', 'coordination'],
    storyHooks: ['allied_network']
  },
  {
    id: 'civilian_rescue',
    category: 'morale',
    moraleType: 'minor_victory',
    title: 'Civilian Rescue Operation',
    description: 'Squad successfully extracted civilians from combat zone. Lives saved, mission accomplished.',
    narrative: 'We fight to protect. Every civilian rescued reminds us why we do this.',
    bonus: true,
    rewards: { supplies: 30, funds: 750 },
    tags: ['rescue', 'purpose'],
    storyHooks: ['civilian_support']
  },
  {
    id: 'enemy_retreat',
    category: 'morale',
    moraleType: 'major_inspiration',
    title: 'Enemy Forces Retreat',
    description: 'Sustained pressure forced enemy to withdraw from key positions. Territory secured.',
    narrative: 'They blinked first. This ground is ours now.',
    bonus: true,
    rewards: { intel: 15, funds: 1500 },
    tags: ['victory', 'territory'],
    storyHooks: []
  }
];

/**
 * FACTION EVENTS
 * Interactions with allied forces, command structure, civilian populations
 */
export const FACTION_EVENTS = [
  {
    id: 'command_pressure',
    category: 'faction',
    title: 'Command Demands Results',
    description: 'Higher command demands accelerated operational tempo. Political pressure mounting.',
    narrative: 'Politicians don\'t fight wars. Soldiers do. But politicians set the timetable.',
    tags: ['command', 'pressure'],
    storyHooks: ['command_tension']
  },
  {
    id: 'allied_request',
    category: 'faction',
    title: 'Allied Force Requests Support',
    description: 'Neighboring allied base requests operational support for joint mission.',
    narrative: 'Cooperation is how we win. But every commitment spreads us thinner.',
    bonus: true,
    tags: ['allies', 'coordination'],
    storyHooks: ['allied_network']
  },
  {
    id: 'resource_dispute',
    category: 'faction',
    title: 'Resource Allocation Dispute',
    description: 'Allied command disputes resource distribution. Tensions rise over limited supplies.',
    narrative: 'Scarcity breeds conflict, even among allies. Leadership must navigate.',
    threat: true,
    tags: ['allies', 'resources'],
    storyHooks: ['allied_tension']
  },
  {
    id: 'civilian_cooperation',
    category: 'faction',
    title: 'Civilian Population Cooperates',
    description: 'Local population provides intelligence and logistical support in exchange for protection.',
    narrative: 'Win the hearts, win the minds. Civilian cooperation is force multiplier.',
    bonus: true,
    rewards: { intel: 15, supplies: 50 },
    tags: ['civilians', 'cooperation'],
    storyHooks: ['civilian_support']
  }
];

/**
 * DISCOVERY EVENTS
 * Finding intelligence, equipment, or tactical advantages
 */
export const DISCOVERY_EVENTS = [
  {
    id: 'enemy_intel',
    category: 'discovery',
    title: 'Enemy Intelligence Cache',
    description: 'Recovered enemy documents reveal detailed battle plans and strategic objectives.',
    narrative: 'Know your enemy. Now we do.',
    rewards: { intel: 20 },
    tags: ['intelligence', 'enemy'],
    storyHooks: ['enemy_plans']
  },
  {
    id: 'advanced_equipment',
    category: 'discovery',
    title: 'Advanced Equipment Prototype',
    description: 'Research team developed prototype equipment that provides significant tactical advantage.',
    narrative: 'Technology is force multiplier. This changes how we fight.',
    rewards: { supplies: 50, intel: 15 },
    tags: ['technology', 'advantage'],
    storyHooks: []
  },
  {
    id: 'strategic_location',
    category: 'discovery',
    title: 'Strategic Position Identified',
    description: 'Reconnaissance identified strategically vital terrain feature. Control of this position would be decisive.',
    narrative: 'Geography dictates strategy. This is the high ground we need.',
    bonus: true,
    rewards: { intel: 30 },
    tags: ['strategy', 'terrain'],
    storyHooks: ['strategic_objective']
  },
  {
    id: 'enemy_weakness',
    category: 'discovery',
    title: 'Enemy Vulnerability Discovered',
    description: 'Analysis revealed critical weakness in enemy defensive strategy.',
    narrative: 'Every fortress has a weak point. We found theirs.',
    bonus: true,
    rewards: { intel: 40 },
    tags: ['intelligence', 'opportunity'],
    storyHooks: ['final_offensive']
  }
];

/**
 * CAMPAIGN EVENTS
 * Major turning points, campaign-wide developments
 */
export const CAMPAIGN_EVENTS = [
  {
    id: 'enemy_ultimatum',
    category: 'campaign',
    title: 'Enemy Ceasefire Offer',
    description: 'Enemy command broadcasts ceasefire proposal. Terms are unclear. Command must decide response.',
    narrative: 'Do we negotiate with an enemy that attacked first? The decision shapes everything.',
    threat: true,
    tags: ['diplomacy', 'choice'],
    storyHooks: ['ceasefire_decision']
  },
  {
    id: 'allied_collapse',
    category: 'campaign',
    title: 'Allied Position Overrun',
    description: 'Major allied base overwhelmed by enemy assault. Hundreds of casualties. Strategic situation deteriorating.',
    narrative: 'We watched them fall. Now we stand alone in this sector.',
    threat: true,
    tags: ['catastrophe', 'isolation'],
    storyHooks: ['desperate_stand']
  },
  {
    id: 'enemy_retreat',
    category: 'campaign',
    title: 'Enemy Strategic Withdrawal',
    description: 'Enemy forces conducting large-scale strategic withdrawal. Opportunity or trap?',
    narrative: 'They\'re pulling back. But retreating enemies are dangerous enemies.',
    bonus: true,
    tags: ['opportunity', 'caution'],
    storyHooks: ['enemy_endgame']
  },
  {
    id: 'reinforcements_arrive',
    category: 'campaign',
    title: 'Major Reinforcements Deployed',
    description: 'Command deployed significant reinforcements to theater. Force strength doubled. Offensive operations now possible.',
    narrative: 'The cavalry arrived. Now we take the fight to them.',
    bonus: true,
    rewards: { intel: 50, funds: 10000 },
    tags: ['reinforcement', 'opportunity'],
    storyHooks: ['final_offensive']
  }
];

const vanillaEventsLibrary = {
  BACKDROP_EVENTS,
  SCHEDULED_EVENTS,
  THREAT_EVENTS,
  MORALE_EVENTS,
  FACTION_EVENTS,
  DISCOVERY_EVENTS,
  CAMPAIGN_EVENTS
};

export default vanillaEventsLibrary;